// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import { mergeWithCss } from "@rpsc/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface ProductDetailsReplacementAnchorStyle {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const productDetailsReplacementAnchorStyles: ProductDetailsReplacementAnchorStyle = {};

interface OwnProps {
    extendedStyles?: ProductDetailsReplacementAnchorStyle;
}

const mapDispatchToProps = { loadProduct };

type Props = OwnProps & HasProductContext & ResolveThunks<typeof mapDispatchToProps>;
const ProductDetailsReplacementAnchor: FC<Props> = ({ extendedStyles, productContext, loadProduct }) => {
    const [styles] = useState(() => mergeWithCss(productDetailsReplacementAnchorStyles, extendedStyles));
    const productId = productContext?.product?.detail?.replacementProductId || undefined;

    const productData = useAppSelector(state => getProductState(state, productId));
    const history = useHistory();

    React.useEffect(() => {
        if (productId && !productData?.isLoading && !productData?.value) {
            loadProduct({
                id: productId,
            });
        }
    }, [productData, productId]);

    if (!productData.value) {
        return null;
    }

    const goToReplacementElement = () => {
        productData.value?.canonicalUrl && history.push(productData.value.canonicalUrl);
    };

    return (
        <StyledBox {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Replacement Product")}</Typography>
            <Typography {...styles.description}>{siteMessage("Custom_Alternative_Item")}</Typography>
            <Link
                {...styles.link}
                onClick={goToReplacementElement}
                data-test-selector="replacement-anchor"
                aria-label={translate("View Replacement Product")}
            >
                {translate("View Replacement Product")}
            </Link>
        </StyledBox>
    );
};

export default withProductContext(connect(null, mapDispatchToProps)(ProductDetailsReplacementAnchor));
